import { JobStatus, SyncSource } from '@bp/bp-graphql-types';
import { Button, Checkbox, DeleteIcon, ImportIcon, Table, TableColumns } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showUserDbErrorToast } from '../../../utils/toasts';

export const LessonJobsTableFragment = graphql(`
  fragment LessonJobsTableFields on Query {
    lessonJobs(where: $lessonJobWhere, options: $lessonJobOptions) {
      __typename
      uuid
      createdAt
      from
      to
      lessonsToProcess
      processed
      status
    }
    schoolYears(where: { uuid: $schoolYearUuid }) {
      uuid
      __typename
      name
      shortName
      start
      end
      createdAt
      managedBy
      autoSync
    }
  }
`);

type PlanungImportLessonsJobTableType = {
  uuid: string;
  created: string;
  from: string;
  until: string;
  processed: number;
  eventsToProcess: number;
  status: JobStatus;
};

type PlanungImportLessonsJobTableProps = {
  query: FragmentType<typeof LessonJobsTableFragment> | undefined;
  handleCancelJob: (x: string) => void;
  handleImport: () => void;
};

const UpdateSchoolyearAutoSyncMutationDocument = graphql(`
  mutation UpdateSchoolyearAutoSync($uuid: ID!, $autoSync: [SyncSource!]!) {
    updateBpSchoolYear(uuid: $uuid, update: { autoSync: $autoSync }) {
      uuid
      __typename
      name
      shortName
      start
      end
      createdAt
      managedBy
      autoSync
    }
  }
`);
export const PlanungImportLessonsJobTable = ({
  query,
  handleCancelJob,
  handleImport,
}: PlanungImportLessonsJobTableProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(LessonJobsTableFragment, query);
  const currentAutoSync = fragment?.schoolYears?.[0]?.autoSync ?? [];
  const currentSchoolYear = fragment?.schoolYears?.[0].uuid;
  const lessonJobs = fragment?.lessonJobs ?? [];
  const [, updateSchoolyear] = useMutation(UpdateSchoolyearAutoSyncMutationDocument);
  const [checkBox, setCheckBox] = useState(currentAutoSync.includes(SyncSource.PlanungLessons));

  const columns: TableColumns<PlanungImportLessonsJobTableType>[] = [
    {
      id: 'created',
      accessorKey: 'created',
      header: t('common.createdAt'),
    },
    {
      id: 'from',
      accessorKey: 'from',
      header: t('planungImport.from'),
    },
    {
      id: 'until',
      accessorKey: 'until',
      header: t('planungImport.until'),
    },
    {
      id: 'processed',
      accessorKey: 'processed',
      header: t('common.processed'),
      accessorFn: (row) => row.processed + '/' + row.eventsToProcess,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('common.status'),
      accessorFn: ({ status }) => getStatusTranslation(status),
      size: 250,
    },
  ];

  const data: PlanungImportLessonsJobTableType[] = lessonJobs.map((job) => {
    const createdAt = dayjs(job.createdAt);
    const from = dayjs(job.from);
    const to = dayjs(job.to);
    return {
      uuid: job.uuid,
      created: createdAt.format('DD.MM.YYYY, HH:mm'),
      from: from.isSame(createdAt, 'day') ? from.format('HH:mm') : from.format('DD.MM.YYYY'),
      until: to.isSame(createdAt, 'day') ? to.format('HH:mm') : to.format('DD.MM.YYYY'),
      processed: job.processed ?? 0,
      eventsToProcess: job.lessonsToProcess ?? 0,
      status: job.status,
    };
  });

  function getStatusTranslation(status: JobStatus): string {
    switch (status) {
      case JobStatus.Cancelled:
        return t('planungImport.status.cancelled');
      case JobStatus.Error:
        return t('planungImport.status.error');
      case JobStatus.Finished:
        return t('planungImport.status.finished');
      case JobStatus.Running:
        return t('planungImport.status.running');
      default:
        return t('planungImport.status.error');
    }
  }

  const updateAutoSyncSetting = async (enableAutoSync: boolean): Promise<void> => {
    let newAutoSync: SyncSource[];

    if (enableAutoSync) {
      if (currentAutoSync.includes(SyncSource.PlanungLessons)) return;
      newAutoSync = [...currentAutoSync, SyncSource.PlanungLessons];
    } else {
      if (!currentAutoSync.includes(SyncSource.PlanungLessons)) return;
      newAutoSync = currentAutoSync.filter((item) => item !== SyncSource.PlanungLessons);
    }

    const resp = await updateSchoolyear({ uuid: currentSchoolYear ?? '--', autoSync: newAutoSync });
    resp.error && showUserDbErrorToast();
  };

  const onCheckboxChange = async (ev: ChangeEvent<HTMLInputElement>) => {
    const checked = ev.target.checked;
    setCheckBox(checked);
    await updateAutoSyncSetting(checked);
  };

  return (
    <Table
      data={data}
      columns={columns}
      hideHeader={data.length === 0}
      customPadding='var(--spacing-7)'
      emptyStateSettings={{ title: '', subtitle: '' }}
      lastCol={(row) => {
        if (row.original.status === JobStatus.Running) {
          return (
            <Button hierarchy={'secondary'} icon={<DeleteIcon />} onClick={() => handleCancelJob(row.original.uuid)} />
          );
        }
      }}
      // lastColWidth='0'
      showActionBar={true}
      actionBarSettings={{
        extendedActionsRight: (
          <>
            <Checkbox
              name='syncCourses'
              checked={checkBox}
              onChange={(ev) => onCheckboxChange(ev)}
              label={t('planungImport.autoSync')}
            />
            <Button type='button' icon={<ImportIcon />} onClick={handleImport}>
              {t('planungImport.importAllLessons')}
            </Button>
          </>
        ),
      }}
      showPagination
      pageSize={20}
    />
  );
};
